import React from 'react';

export default function Mission() {
  const pillars = [
    {
      icon: '🎯',
      title: 'Precision Diagnostics',
      description: 'Enable early disease detection through multimodal fusion of acoustic biomarkers, radiology imaging, and pathology data.',
    },
    {
      icon: '🧠',
      title: 'Explainable AI (XAI)',
      description: 'Build trust through transparent AI with attention visualization, SHAP/LIME integration, and interpretable models.',
    },
    {
      icon: '🌐',
      title: 'Global Accessibility',
      description: 'Make advanced AI diagnostics available to healthcare systems in India and worldwide, including underserved regions.',
    },
    {
      icon: '🔬',
      title: 'Clinical Validation',
      description: 'Commit to rigorous clinical validation studies across multiple medical centers for regulatory clearance.',
    },
    {
      icon: '🤝',
      title: 'Clinician Empowerment',
      description: 'Enhance clinician expertise with AI-driven insights that support, not replace, medical decision-making.',
    },
    {
      icon: '🔒',
      title: 'Data Privacy',
      description: 'Protect patient data with HIPAA/GDPR compliance and industry-leading security standards.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-tealLight/30 to-paper text-ink antialiased selection:bg-teal/20 selection:text-ink">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 font-sans">
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-tealLight border border-teal/20 text-teal text-xs font-semibold uppercase tracking-wider">
              Our Mission
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-sans font-bold leading-tight tracking-tight text-center text-teal">
            Our Mission
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-inkSoft max-w-3xl mx-auto leading-relaxed text-center">
            To transform healthcare through multimodal AI-powered diagnostics,
            bridging the gap between cutting-edge research and clinical practice.
          </p>
        </div>

        <div className="bg-ink text-white rounded-xl p-8 mb-16 shadow-lg">
          <blockquote className="text-xl italic text-tealMid text-center max-w-4xl mx-auto">
            "Empowering clinicians with multimodal AI insights to detect diseases earlier,
            with higher accuracy across acoustic, radiology, and pathology data streams."
          </blockquote>
          <div className="text-center mt-4 text-teal-300 text-sm">— Ram Samudrala, Founder & CEO</div>
        </div>

        <div className="mb-16">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg bg-tealLight border border-teal/20 flex items-center justify-center text-lg">⭐</span>
              <h2 className="text-2xl font-bold text-ink tracking-tight">Our Guiding Pillars</h2>
            </div>
            <span className="inline-flex items-center gap-1.5 bg-tealLight text-teal text-[10px] font-bold tracking-wider uppercase px-3 py-1.5 rounded-md border border-teal/20">
              <span className="w-1.5 h-1.5 rounded-full bg-teal"></span>
              Core Principles
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillars.map((pillar, index) => (
              <div
                key={index}
                className="group relative bg-white border border-rule rounded-xl p-6 shadow-[4px_4px_0_0_rgba(20,184,166,0.18)] hover:shadow-[6px_6px_0_0_rgba(20,184,166,0.3)] hover:-translate-y-0.5 hover:-translate-x-0.5 transition-all duration-200 text-center"
              >
                <div className="text-4xl mb-4">{pillar.icon}</div>
                <h3 className="text-lg font-bold text-ink group-hover:text-teal transition-colors mb-2">{pillar.title}</h3>
                <p className="text-sm text-inkSoft leading-relaxed">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 p-8 bg-white border border-rule rounded-xl shadow-[4px_4px_0_0_rgba(20,184,166,0.18)] text-center">
          <h3 className="text-xl font-bold text-ink mb-4">Our Commitment to MedTech Innovation</h3>
          <p className="text-inkSoft max-w-3xl mx-auto leading-relaxed">
            We are committed to advancing medical AI through open collaboration with clinicians, rigorous scientific validation,
            and a patient-centered approach. Our Diagnostic Intelligence platform represents the next generation of multimodal fusion architectures,
            ontology-guided AI systems, and clinical diagnostic MVPs that will shape the future of precision medicine.
          </p>
        </div>
      </div>
    </div>
  );
}
